import {
  DEFAULT_COUNT_THRESHOLD,
  DEFAULT_TIME_THRESHOLD_SECONDS,
  FIXED_DRIVE_OFF_THRESHOLD,
  GRID_SIZE,
} from '../constants';
import { type GridPosition } from './gridPositions';
import type { SDMCountWidget, SDMTimeWidget } from './types';

export type TemplateCategory = 'Restaurant' | 'Car Wash' | 'Convenience Store' | 'Quick Oil Change';

export interface PredefinedLayoutWidget {
  title: string;
  tagNames: string[];
  config: SDMCountWidget | SDMTimeWidget;
  position: GridPosition;
}

export interface PredefinedLayout {
  id: string;
  category: TemplateCategory;
  name: string;
  description: string;
  jiraKey: string;
  widgets: ReadonlyArray<PredefinedLayoutWidget>;
}

const H2 = GRID_SIZE / 2;
const H3 = GRID_SIZE / 3;
const W2 = GRID_SIZE / 2;
const W3 = GRID_SIZE / 3;

/** 1 widget — full screen */
const POSITIONS_1: GridPosition[] = [{ x: 0, y: 0, w: GRID_SIZE, h: GRID_SIZE }];

/** 2 widgets — stacked full-width rows */
const POSITIONS_2: GridPosition[] = [
  { x: 0, y: 0, w: GRID_SIZE, h: H2 },
  { x: 0, y: H2, w: GRID_SIZE, h: H2 },
];

/** 4 widgets — one top, two middle, one bottom */
const POSITIONS_4: GridPosition[] = [
  { x: 0, y: 0, w: GRID_SIZE, h: H3 },
  { x: 0, y: H3, w: W2, h: H3 },
  { x: W2, y: H3, w: W2, h: H3 },
  { x: 0, y: 2 * H3, w: GRID_SIZE, h: H3 },
];

/** 5 widgets — one top, two middle, two bottom */
const POSITIONS_5: GridPosition[] = [
  { x: 0, y: 0, w: GRID_SIZE, h: H3 },
  { x: 0, y: H3, w: W2, h: H3 },
  { x: W2, y: H3, w: W2, h: H3 },
  { x: 0, y: 2 * H3, w: W2, h: H3 },
  { x: W2, y: 2 * H3, w: W2, h: H3 },
];

/**
 * 8 widgets — 3×3 grid (SL-13265): bays across the top, queue metrics on the middle
 * row (left/right), daily metrics across the bottom.
 */
const POSITIONS_3_BAY: GridPosition[] = [
  { x: 0, y: 0, w: W3, h: H3 }, // Bay 1 — top left
  { x: W3, y: 0, w: W3, h: H3 }, // Bay 2 — top middle
  { x: 2 * W3, y: 0, w: W3, h: H3 }, // Bay 3 — top right
  { x: W2, y: H3, w: W2, h: H3 }, // Queue Time — middle right (half row)
  { x: 0, y: H3, w: W2, h: H3 }, // Queue Count — middle left (half row)
  { x: 0, y: 2 * H3, w: W3, h: H3 }, // Total Vehicles Serviced — bottom left
  { x: W3, y: 2 * H3, w: W3, h: H3 }, // Average Service Time — bottom middle
  { x: 2 * W3, y: 2 * H3, w: W3, h: H3 }, // Drive-offs — bottom right
];

type WidgetDraft = Omit<PredefinedLayoutWidget, 'position'>;

function templateWidgets(
  widgets: ReadonlyArray<WidgetDraft>,
  positions: ReadonlyArray<GridPosition>,
): PredefinedLayoutWidget[] {
  if (widgets.length !== positions.length) {
    throw new Error(
      `Template widget count (${widgets.length}) does not match position count (${positions.length})`,
    );
  }
  return widgets.map((widget, index) => ({
    ...widget,
    position: { ...positions[index] },
  }));
}

const timeWidget = (
  horizon: SDMTimeWidget['horizon'],
  threshold: number = DEFAULT_TIME_THRESHOLD_SECONDS,
): SDMTimeWidget => ({
  type: 'TIME',
  threshold,
  horizon,
});

const countWidget = (
  horizon: SDMCountWidget['horizon'],
  threshold: number = DEFAULT_COUNT_THRESHOLD,
): SDMCountWidget => ({
  type: 'COUNT',
  threshold,
  horizon,
});

const driveOffWidget = (horizon: SDMCountWidget['horizon'] = 'currentDay'): SDMCountWidget => ({
  type: 'DRIVE_OFF',
  threshold: FIXED_DRIVE_OFF_THRESHOLD,
  horizon,
});

export const PREDEFINED_LAYOUTS: ReadonlyArray<PredefinedLayout> = [
  {
    id: 'restaurant-single-lane',
    category: 'Restaurant',
    name: 'Single Lane Drive Thru',
    description: 'Tracks real-time service times, stack count, and hourly throughput.',
    jiraKey: 'SL-13258',
    widgets: templateWidgets(
      [
        { title: 'Service Window', tagNames: ['Service Window'], config: timeWidget('realtime') },
        { title: 'Menu Board', tagNames: ['Menu Board'], config: timeWidget('realtime') },
        {
          title: 'Average Service Window Time',
          tagNames: ['Service Window'],
          config: timeWidget('1h'),
        },
        { title: 'Stack Count', tagNames: ['Stack'], config: countWidget('realtime') },
        {
          title: 'Throughput (Last Hour)',
          tagNames: ['Service Window'],
          config: countWidget('1h'),
        },
      ],
      POSITIONS_5,
    ),
  },
  {
    id: 'restaurant-dual-menu',
    category: 'Restaurant',
    name: 'Dual Lane Drive Thru',
    description: 'Tracks real-time service times, stack count, and hourly throughput.',
    jiraKey: 'SL-13259',
    widgets: templateWidgets(
      [
        { title: 'Service Window', tagNames: ['Service Window'], config: timeWidget('realtime') },
        { title: 'Menu Board 1', tagNames: ['Menu Board'], config: timeWidget('realtime') },
        { title: 'Menu Board 2', tagNames: ['Menu Board 2'], config: timeWidget('realtime') },
        { title: 'Stack Count', tagNames: ['Stack'], config: countWidget('realtime') },
        {
          title: 'Throughput (Last Hour)',
          tagNames: ['Service Window'],
          config: countWidget('1h'),
        },
      ],
      POSITIONS_5,
    ),
  },
  {
    id: 'car-wash-approximate-wait-time',
    category: 'Car Wash',
    name: 'Car Wash',
    description: 'Tracks approximate wait time from queue position.',
    jiraKey: 'SL-13260',
    widgets: templateWidgets(
      [
        {
          title: 'Approximate Wait Time From This Point',
          tagNames: ['Queue'],
          config: timeWidget('1h'),
        },
      ],
      POSITIONS_1,
    ),
  },
  {
    id: 'c-store-drive-thru',
    category: 'Convenience Store',
    name: 'Basic Drive Thru',
    description: 'Tracks current and average speed of service.',
    jiraKey: 'SL-13263',
    widgets: templateWidgets(
      [
        {
          title: 'Current Service Time',
          tagNames: ['Service Window'],
          config: timeWidget('realtime'),
        },
        {
          title: 'Average Speed of Service',
          tagNames: ['Service Window'],
          config: timeWidget('1h'),
        },
      ],
      POSITIONS_2,
    ),
  },
  {
    id: 'c-store-pump-zones',
    category: 'Convenience Store',
    name: 'Pump Zones',
    description: 'Tracks real-time service time per pump.',
    jiraKey: 'SL-13264',
    widgets: templateWidgets(
      [
        { title: 'Pump 1', tagNames: ['Pump 1'], config: timeWidget('realtime') },
        { title: 'Pump 2', tagNames: ['Pump 2'], config: timeWidget('realtime') },
        { title: 'Pump 3', tagNames: ['Pump 3'], config: timeWidget('realtime') },
        { title: 'Pump 4', tagNames: ['Pump 4'], config: timeWidget('realtime') },
        { title: 'Pump 5', tagNames: ['Pump 5'], config: timeWidget('realtime') },
      ],
      POSITIONS_5,
    ),
  },
  {
    id: 'automotive-2-bay',
    category: 'Quick Oil Change',
    name: '2 Bay',
    description: 'Tracks service time per bay, queue time, and queue count.',
    jiraKey: 'SL-13267',
    widgets: templateWidgets(
      [
        { title: 'Bay 1 Service Time', tagNames: ['Bay 1'], config: timeWidget('realtime') },
        { title: 'Bay 2 Service Time', tagNames: ['Bay 2'], config: timeWidget('realtime') },
        { title: 'Queue Time', tagNames: ['Queue'], config: timeWidget('1h') },
        { title: 'Queue Count', tagNames: ['Queue'], config: countWidget('realtime') },
      ],
      POSITIONS_4,
    ),
  },
  {
    id: 'automotive-3-bay',
    category: 'Quick Oil Change',
    name: '3 Bay',
    description:
      'Tracks real-time bay service times, queue metrics, daily throughput, average service time, and drive-offs.',
    jiraKey: 'SL-13265',
    widgets: templateWidgets(
      [
        { title: 'Bay 1 Service Time', tagNames: ['Bay 1'], config: timeWidget('realtime') },
        { title: 'Bay 2 Service Time', tagNames: ['Bay 2'], config: timeWidget('realtime') },
        { title: 'Bay 3 Service Time', tagNames: ['Bay 3'], config: timeWidget('realtime') },
        { title: 'Queue Time', tagNames: ['Queue'], config: timeWidget('1h') },
        { title: 'Queue Count', tagNames: ['Queue'], config: countWidget('realtime') },
        {
          title: 'Total Vehicles Serviced',
          tagNames: ['Bay 1', 'Bay 2', 'Bay 3'],
          config: countWidget('currentDay'),
        },
        {
          title: 'Average Service Time',
          tagNames: ['Bay 1', 'Bay 2', 'Bay 3'],
          config: timeWidget('currentDay'),
        },
        { title: 'Drive-offs', tagNames: ['Queue'], config: driveOffWidget('currentDay') },
      ],
      POSITIONS_3_BAY,
    ),
  },
  {
    id: 'automotive-4-bay',
    category: 'Quick Oil Change',
    name: '4 Bay',
    description: 'Tracks service time per bay, and queue time.',
    jiraKey: 'SL-13266',
    widgets: templateWidgets(
      [
        { title: 'Queue Time', tagNames: ['Queue'], config: timeWidget('1h') },
        { title: 'Bay 1 Service Time', tagNames: ['Bay 1'], config: timeWidget('realtime') },
        { title: 'Bay 2 Service Time', tagNames: ['Bay 2'], config: timeWidget('realtime') },
        { title: 'Bay 3 Service Time', tagNames: ['Bay 3'], config: timeWidget('realtime') },
        { title: 'Bay 4 Service Time', tagNames: ['Bay 4'], config: timeWidget('realtime') },
      ],
      POSITIONS_5,
    ),
  },
];

export const TEMPLATE_CATEGORY_ORDER: ReadonlyArray<TemplateCategory> = [
  'Restaurant',
  'Convenience Store',
  'Quick Oil Change',
  'Car Wash',
];

export function getPredefinedLayoutById(id: string): PredefinedLayout | undefined {
  return PREDEFINED_LAYOUTS.find((t) => t.id === id);
}
